import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import TreeDiagram, { isTreeDiagram } from './TreeDiagram';
import { MermaidDiagram } from './mermaid/MermaidDiagram';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  onReady?: () => void;
}

export default function MarkdownRenderer({ content, className = '', onReady }: MarkdownRendererProps) {
  // Count expected mermaid diagrams so onReady only fires once every one of
  // them has finished rendering (success or error) — CollectionPdfExport.tsx
  // relies on onReady to know when it's safe to print/export to PDF.
  const expectedMermaidCount = useMemo(
    () => (content.match(/```mermaid\b/g) || []).length,
    [content],
  );
  const settledCountRef = useRef(0);

  // CollectionPdfExport.tsx re-renders its parent (and therefore every
  // sibling MarkdownRenderer) each time a diagram settles, which gives
  // `onReady` a new function identity on every render. `components` below
  // must stay referentially stable across those re-renders — react-markdown
  // uses `components.code`/`components.pre` as the actual React element
  // type, so a new object identity there force-remounts every custom-
  // rendered element (including in-flight MermaidDiagram renders) on every
  // settle, causing an infinite remount loop that never lets diagrams finish.
  // Reading the latest onReady through a ref keeps handleDiagramSettled (and
  // therefore `components`) stable regardless of the caller's own identity.
  const onReadyRef = useRef(onReady);
  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    settledCountRef.current = 0;
    if (expectedMermaidCount === 0) {
      onReadyRef.current?.();
    }
  }, [content, expectedMermaidCount]);

  const handleDiagramSettled = useCallback(() => {
    settledCountRef.current += 1;
    if (settledCountRef.current >= expectedMermaidCount) {
      onReadyRef.current?.();
    }
  }, [expectedMermaidCount]);

  // Detect dark mode
  const isDark = typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark');

  // Memoized so react-markdown (which uses these functions as the actual
  // React element type) doesn't force-remount every custom-rendered element
  // on renders that don't actually change output — see handleDiagramSettled
  // above for why that matters.
  const components = useMemo(() => ({
          // react-markdown wraps every fenced code block in a <pre> by default;
          // our `code` renderer below already returns a fully-styled element
          // (MermaidDiagram / TreeDiagram / SyntaxHighlighter) for every case,
          // so the extra <pre> wrapper is dropped here. Without this, the
          // browser's default `pre { font-family: monospace }` leaks into
          // mermaid's `fontFamily: 'inherit'` SVG text and clips diagram labels.
          pre: ({ children }) => <>{children}</>,
          // Custom code block renderer
          code({ node, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            const codeString = String(children).replace(/\n$/, '');
            
            // Handle mermaid diagrams
            if (language === 'mermaid') {
              return <MermaidDiagram code={codeString} onSettled={handleDiagramSettled} />;
            }

            // Check if it's an ASCII tree diagram (no language specified or plaintext)
            if ((!match || language === 'text' || language === 'plaintext') && isTreeDiagram(codeString)) {
              return <TreeDiagram content={codeString} />;
            }
            
            // Inline code
            if (!match) {
              return (
                <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                  {children}
                </code>
              );
            }
            
            // Code block with syntax highlighting
            return (
              <SyntaxHighlighter
                style={isDark ? oneDark : oneLight}
                language={language}
                PreTag="div"
                className="rounded-lg my-4 text-sm"
                showLineNumbers={codeString.split('\n').length > 3}
              >
                {codeString}
              </SyntaxHighlighter>
            );
          },
          // Custom heading renderers to ensure proper styling
          h1: ({ children, ...props }) => (
            <h1 className="text-3xl font-bold mt-8 mb-4 text-foreground" {...props}>{children}</h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 className="text-2xl font-semibold mt-8 mb-4 pb-2 border-b border-primary/30 text-primary" {...props}>{children}</h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 className="text-xl font-semibold mt-6 mb-3 text-foreground" {...props}>{children}</h3>
          ),
          h4: ({ children, ...props }) => (
            <h4 className="text-lg font-semibold mt-4 mb-2 text-foreground" {...props}>{children}</h4>
          ),
          // Paragraph
          p: ({ children, ...props }) => (
            <p className="my-4 leading-7 text-foreground/90" {...props}>{children}</p>
          ),
          // Links
          a: ({ href, children, ...props }) => (
            <a 
              href={href} 
              className="text-primary hover:underline" 
              target={href?.startsWith('http') ? '_blank' : undefined}
              rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              {...props}
            >
              {children}
            </a>
          ),
          // Lists
          ul: ({ children, ...props }) => (
            <ul className="my-4 ml-6 list-disc space-y-2" {...props}>{children}</ul>
          ),
          ol: ({ children, ...props }) => (
            <ol className="my-4 ml-6 list-decimal space-y-2" {...props}>{children}</ol>
          ),
          li: ({ children, ...props }) => (
            <li className="leading-7" {...props}>{children}</li>
          ),
          // Blockquote
          blockquote: ({ children, ...props }) => (
            <blockquote className="border-l-4 border-primary/50 pl-4 my-4 italic text-muted-foreground" {...props}>
              {children}
            </blockquote>
          ),
          // Tables
          table: ({ children, ...props }) => (
            <div className="overflow-x-auto my-6">
              <table className="w-full border-collapse border border-border" {...props}>
                {children}
              </table>
            </div>
          ),
          thead: ({ children, ...props }) => (
            <thead className="bg-muted" {...props}>{children}</thead>
          ),
          th: ({ children, ...props }) => (
            <th className="border border-border px-4 py-2 text-left font-semibold" {...props}>{children}</th>
          ),
          td: ({ children, ...props }) => (
            <td className="border border-border px-4 py-2" {...props}>{children}</td>
          ),
          // Horizontal rule
          hr: () => <hr className="my-8 border-border" />,
          // Images
          img: ({ src, alt, ...props }) => (
            <img 
              src={src} 
              alt={alt} 
              className="max-w-full h-auto rounded-lg my-4" 
              loading="lazy"
              {...props}
            />
          ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [isDark, handleDiagramSettled]);

  return (
    <div className={`doc-prose ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Extract table of contents from markdown content
 * Returns an array of heading items with id, text, and depth
 */
export function extractTocFromMarkdown(markdown: string): { id: string; text: string; depth: number }[] {
  const items: { id: string; text: string; depth: number }[] = [];
  
  // Match headings (## and ### only for TOC)
  const headingRegex = /^(#{2,3})\s+(.+)$/gm;
  let match;
  
  while ((match = headingRegex.exec(markdown)) !== null) {
    const depth = match[1].length;
    const text = match[2].trim();
    
    // Generate slug matching rehype-slug behavior exactly
    // rehype-slug uses github-slugger which:
    // 1. Converts to lowercase
    // 2. Replaces spaces with hyphens
    // 3. Removes anything that isn't alphanumeric, space, or hyphen
    // 4. Does NOT collapse multiple hyphens (& becomes nothing, leaving --)
    const id = text
      .toLowerCase()
      .replace(/\s+/g, '-')           // Replace spaces with hyphens first
      .replace(/[^\w-]/g, '')         // Remove non-word chars except hyphens
      .replace(/--+/g, '-')           // Collapse multiple hyphens
      .replace(/^-|-$/g, '')          // Remove leading/trailing hyphens
      .replace(/^(\d)/, '_$1');       // Prefix with _ if starts with number
    
    if (id && text) {
      items.push({ id, text, depth });
    }
  }
  
  return items;
}
